import { useEffect, useMemo, useState } from 'react';
import { otApi } from '../api';
import { formatMoney } from '../lib/format';

type Job = {
  id: string;
  title: string;
  status: string;
  priority: string;
  scheduled_date: string | null;
  scheduled_time: string | null;
  total_pence: number;
  customer_id: string | null;
  address_line1: string | null;
  city: string | null;
  postcode: string | null;
  created_at: string;
};

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending:     'bg-ink-border text-ink-text',
    scheduled:   'bg-blue-900/40 text-blue-300',
    in_progress: 'bg-amber-900/40 text-amber-300',
    completed:   'bg-green-900/40 text-green-300',
    cancelled:   'bg-red-900/40 text-red-300',
    invoiced:    'bg-purple-900/40 text-purple-300',
  };
  const cls = styles[status] || 'bg-ink-border text-ink-text';
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{status}</span>;
}

export default function JobsPage() {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await otApi<Job[] | { jobs: Job[] }>('/jobs');
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data as { jobs: Job[] }).jobs || [];
        setJobs(list);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!jobs) return [];
    return jobs.filter(j => {
      if (statusFilter !== 'all' && j.status !== statusFilter) return false;
      if (q) {
        const needle = q.toLowerCase();
        const hay = [j.title, j.address_line1, j.postcode].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [jobs, q, statusFilter]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold">Jobs</h1>
        <button className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-2 rounded">+ New Job</button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
        <input placeholder="Search title / address / postcode" value={q} onChange={e => setQ(e.target.value)}
               className="flex-1 bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="bg-ink-bg border border-ink-border text-ink-text rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-600">
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="invoiced">Invoiced</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-950/50 border border-red-900 text-red-300 rounded p-3 text-sm mb-4">
          Error loading jobs: {error}
        </div>
      )}
      {jobs === null && !error && <div className="text-ink-muted text-sm">Loading jobs…</div>}
      {jobs !== null && !error && filtered.length === 0 && (
        <div className="bg-ink-card border border-ink-border rounded p-8 text-center text-ink-muted text-sm">
          {jobs.length === 0 ? 'No jobs yet.' : 'No jobs match your filter.'}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="bg-ink-card border border-ink-border rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-bg border-b border-ink-border text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Title</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Scheduled</th>
                <th className="text-left px-4 py-2 font-semibold">Address</th>
                <th className="text-right px-4 py-2 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(j => (
                <tr key={j.id} className="border-b border-ink-border last:border-0 hover:bg-ink-hover/30">
                  <td className="px-4 py-2 font-medium">{j.title || '(untitled)'}</td>
                  <td className="px-4 py-2"><StatusPill status={j.status} /></td>
                  <td className="px-4 py-2 text-ink-muted">
                    {j.scheduled_date ? `${j.scheduled_date}${j.scheduled_time ? ' · ' + j.scheduled_time.slice(0,5) : ''}` : '—'}
                  </td>
                  <td className="px-4 py-2 text-ink-muted">
                    {[j.address_line1, j.city, j.postcode].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatMoney(j.total_pence || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-ink-bg border-t border-ink-border text-xs text-ink-muted">
            {filtered.length} of {jobs?.length} jobs
          </div>
        </div>
      )}
    </div>
  );
}
